import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import QMSLayout from "../components/QMSLayout";
import { API, useAuth } from "../App";
import axios from "axios";
import {
  FileCheck, Plus, Loader2, CheckCircle2, XCircle, Clock, AlertTriangle,
  ChevronDown, ChevronRight, Receipt, FileText, Target, ClipboardCheck
} from "lucide-react";

const ProjectClosurePage = () => {
  const { t } = useTranslation('operations');
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [closures, setClosures] = useState([]);
  const [selected, setSelected] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [showInvoice, setShowInvoice] = useState(false);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [expandedSections, setExpandedSections] = useState({ deliverables: true, milestones: true });
  const [form, setForm] = useState({ project_id: "", title: "", proposal_id: "" });
  const [invoiceForm, setInvoiceForm] = useState({ client_country: "US", items: [{ description: "", amount: "" }], notes: "" });
  const [approvalComment, setApprovalComment] = useState("");

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [projRes, propRes, closRes] = await Promise.all([
        axios.get(`${API}/projects`, { withCredentials: true }),
        axios.get(`${API}/proposals`, { withCredentials: true }),
        axios.get(`${API}/project-closures`, { withCredentials: true }),
      ]);
      setProjects(projRes.data);
      setProposals(propRes.data);
      setClosures(closRes.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const handleGenerate = async () => {
    if (!form.project_id || !form.title) return;
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/project-closure/generate`, form, { withCredentials: true });
      setSelected(res.data);
      setShowCreate(false);
      loadAll();
    } catch (e) {
      console.error(e);
      alert("Failed to generate closure report");
    }
    setGenerating(false);
  };

  const handleApproval = async (action, signerRole) => {
    if (!selected) return;
    try {
      const res = await axios.post(
        `${API}/project-closures/${selected.closure_id}/approve`,
        { action, signer_role: signerRole, comments: approvalComment },
        { withCredentials: true }
      );
      const updated = { ...selected };
      updated.approvals[signerRole] = { status: action, signer: user?.user_id, date: new Date().toISOString(), comments: approvalComment };
      updated.status = res.data.status;
      updated.invoice_eligible = res.data.invoice_eligible;
      setSelected(updated);
      setApprovalComment("");
      loadAll();
    } catch (e) { console.error(e); }
  };

  const handleCreateInvoice = async () => {
    if (!selected) return;
    try {
      await axios.post(`${API}/invoices/generate`, {
        closure_id: selected.closure_id,
        client_name: selected.client_name,
        client_country: invoiceForm.client_country,
        items: invoiceForm.items.filter(i => i.description),
        notes: invoiceForm.notes
      }, { withCredentials: true });
      setShowInvoice(false);
      alert("Invoice generated successfully");
    } catch (e) {
      console.error(e);
      alert(e.response?.data?.detail || "Failed to generate invoice");
    }
  };

  const toggleSection = (s) => setExpandedSections(prev => ({ ...prev, [s]: !prev[s] }));

  const report = selected?.report || {};
  const approvals = selected?.approvals || {};

  const getStatusBadge = (status) => {
    const map = {
      draft: { color: "#8B95A5", bg: "rgba(139,149,165,0.1)", label: "Draft" },
      "pending-approval": { color: "#FBBF24", bg: "rgba(251,191,36,0.1)", label: "Pending Approval" },
      approved: { color: "#4ADE80", bg: "rgba(74,222,128,0.1)", label: "Approved" },
      rejected: { color: "#F87171", bg: "rgba(248,113,113,0.1)", label: "Rejected" },
    };
    const cfg = map[status] || map.draft;
    return <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: cfg.bg, color: cfg.color }}>{cfg.label}</span>;
  };

  return (
    <QMSLayout title={t('closure.title')}>
      <div className="space-y-6" data-testid="project-closure-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <p className="text-[#8B95A5] text-sm">
            Generate closure reports, approve deliverables, and manage invoicing
          </p>
          <button
            onClick={() => setShowCreate(true)}
            className="flex items-center gap-2 px-4 py-2.5 bg-[#36A9E1] hover:bg-[#2d8fbf] text-white text-sm font-medium rounded-[3px] transition-colors"
            data-testid="new-closure-btn"
          >
            <Plus size={16} />
            New Closure Report
          </button>
        </div>

        {/* Create Modal */}
        {showCreate && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
            <div className="bg-[#1F222D] border border-[rgba(255,255,255,0.07)] rounded-[3px] w-full max-w-lg" data-testid="create-closure-modal">
              <div className="p-6 border-b border-[rgba(255,255,255,0.07)]">
                <h2 className="text-lg font-['Syne'] font-bold text-white">Generate Closure Report</h2>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-[#CBD5E1] text-sm mb-1.5">Project</label>
                  <select
                    value={form.project_id}
                    onChange={e => setForm({...form, project_id: e.target.value})}
                    className="w-full px-3 py-2.5 bg-[#111318] border border-[rgba(255,255,255,0.1)] rounded-[3px] text-white text-sm outline-none"
                    data-testid="closure-project-select"
                  >
                    <option value="">Select Project</option>
                    {projects.map(p => (
                      <option key={p.project_id} value={p.project_id}>{p.name} ({p.client_name})</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-[#CBD5E1] text-sm mb-1.5">Report Title</label>
                  <input
                    value={form.title}
                    onChange={e => setForm({...form, title: e.target.value})}
                    placeholder="e.g., Project Closure - PharmaCorp GMP"
                    className="w-full px-3 py-2.5 bg-[#111318] border border-[rgba(255,255,255,0.1)] rounded-[3px] text-white text-sm outline-none"
                    data-testid="closure-title-input"
                  />
                </div>
                <div>
                  <label className="block text-[#CBD5E1] text-sm mb-1.5">Link Proposal (Optional)</label>
                  <select
                    value={form.proposal_id}
                    onChange={e => setForm({...form, proposal_id: e.target.value})}
                    className="w-full px-3 py-2.5 bg-[#111318] border border-[rgba(255,255,255,0.1)] rounded-[3px] text-white text-sm outline-none"
                  >
                    <option value="">None</option>
                    {proposals.map(p => (
                      <option key={p.proposal_id} value={p.proposal_id}>{p.proposal?.title || p.proposal_id}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="p-6 border-t border-[rgba(255,255,255,0.07)] flex justify-end gap-3">
                <button onClick={() => setShowCreate(false)} className="px-4 py-2 text-[#8B95A5] text-sm">{t('common:actions.cancel')}</button>
                <button
                  onClick={handleGenerate}
                  disabled={generating || !form.project_id || !form.title}
                  className="flex items-center gap-2 px-5 py-2.5 bg-[#36A9E1] disabled:opacity-50 text-white text-sm font-medium rounded-[3px]"
                  data-testid="generate-closure-btn"
                >
                  {generating ? <><Loader2 size={16} className="animate-spin" /> Generating...</> : <><FileCheck size={16} /> Generate</>}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Invoice Modal */}
        {showInvoice && selected?.invoice_eligible && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
            <div className="bg-[#1F222D] border border-[rgba(255,255,255,0.07)] rounded-[3px] w-full max-w-lg" data-testid="invoice-modal">
              <div className="p-6 border-b border-[rgba(255,255,255,0.07)]">
                <h2 className="text-lg font-['Syne'] font-bold text-white">Generate Invoice</h2>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-[#CBD5E1] text-sm mb-1.5">Client Country</label>
                  <select
                    value={invoiceForm.client_country}
                    onChange={e => setInvoiceForm({...invoiceForm, client_country: e.target.value})}
                    className="w-full px-3 py-2.5 bg-[#111318] border border-[rgba(255,255,255,0.1)] rounded-[3px] text-white text-sm outline-none"
                  >
                    <option value="US">USA - CHARSTAN Intelligence Platform</option>
                    <option value="BR">Brasil - CHARSTAN CONSULTORIA DE QUALIDADE LTDA</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[#CBD5E1] text-sm mb-1.5">Line Items</label>
                  {invoiceForm.items.map((item, i) => (
                    <div key={i} className="flex gap-2 mb-2">
                      <input
                        value={item.description}
                        onChange={e => {
                          const items = [...invoiceForm.items];
                          items[i].description = e.target.value;
                          setInvoiceForm({...invoiceForm, items});
                        }}
                        placeholder={t('operations:projectClosure.description')}
                        className="flex-1 px-3 py-2 bg-[#111318] border border-[rgba(255,255,255,0.1)] rounded-[3px] text-white text-sm outline-none"
                      />
                      <input
                        value={item.amount}
                        onChange={e => {
                          const items = [...invoiceForm.items];
                          items[i].amount = e.target.value;
                          setInvoiceForm({...invoiceForm, items});
                        }}
                        placeholder={t('operations:projectClosure.amount')}
                        className="w-28 px-3 py-2 bg-[#111318] border border-[rgba(255,255,255,0.1)] rounded-[3px] text-white text-sm outline-none"
                      />
                    </div>
                  ))}
                  <button
                    onClick={() => setInvoiceForm({...invoiceForm, items: [...invoiceForm.items, { description: "", amount: "" }]})}
                    className="text-[#38BDF8] text-xs hover:underline"
                  >
                    + Add Line Item
                  </button>
                </div>
                <div>
                  <label className="block text-[#CBD5E1] text-sm mb-1.5">Notes</label>
                  <textarea
                    value={invoiceForm.notes}
                    onChange={e => setInvoiceForm({...invoiceForm, notes: e.target.value})}
                    rows={2}
                    className="w-full px-3 py-2 bg-[#111318] border border-[rgba(255,255,255,0.1)] rounded-[3px] text-white text-sm outline-none resize-none"
                  />
                </div>
              </div>
              <div className="p-6 border-t border-[rgba(255,255,255,0.07)] flex justify-end gap-3">
                <button onClick={() => setShowInvoice(false)} className="px-4 py-2 text-[#8B95A5] text-sm">{t('common:actions.cancel')}</button>
                <button onClick={handleCreateInvoice} className="flex items-center gap-2 px-5 py-2.5 bg-[#4ADE80] text-[#111318] text-sm font-medium rounded-[3px]">
                  <Receipt size={16} /> Create Invoice
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* List */}
          <div className="lg:col-span-1 space-y-3">
            <h3 className="text-sm font-medium text-[#8B95A5] uppercase tracking-wider">Closure Reports</h3>
            {loading ? (
              <div className="flex items-center justify-center py-12"><Loader2 className="animate-spin text-[#36A9E1]" size={24} /></div>
            ) : closures.length === 0 ? (
              <div className="bg-[#1F222D] border border-[rgba(255,255,255,0.07)] rounded-[3px] p-8 text-center">
                <FileCheck className="mx-auto mb-3 text-[#8B95A5]" size={32} />
                <p className="text-[#8B95A5] text-sm">No closure reports yet</p>
              </div>
            ) : (
              closures.map(c => (
                <div
                  key={c.closure_id}
                  onClick={() => setSelected(c)}
                  className={`bg-[#1F222D] border rounded-[3px] p-4 cursor-pointer transition-all hover:border-[#36A9E1]/40 ${
                    selected?.closure_id === c.closure_id ? "border-[#36A9E1]" : "border-[rgba(255,255,255,0.07)]"
                  }`}
                  data-testid={`closure-item-${c.closure_id}`}
                >
                  <h4 className="text-white text-sm font-medium truncate">{c.title}</h4>
                  <p className="text-[#8B95A5] text-xs mt-1">{c.client_name} - {c.sector}</p>
                  <div className="flex items-center justify-between mt-2">
                    {getStatusBadge(c.status)}
                    {c.invoice_eligible && <span className="text-[#4ADE80] text-xs flex items-center gap-1"><Receipt size={10} /> Invoice Ready</span>}
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Detail */}
          <div className="lg:col-span-2">
            {selected ? (
              <div className="space-y-4">
                {/* Header */}
                <div className="bg-[#1F222D] border border-[rgba(255,255,255,0.07)] rounded-[3px] p-5">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="text-white font-['Syne'] font-bold">{selected.title}</h3>
                      <p className="text-[#8B95A5] text-sm mt-1">{selected.client_name} | {selected.sector}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      {getStatusBadge(selected.status)}
                      {selected.invoice_eligible && (
                        <button
                          onClick={() => setShowInvoice(true)}
                          className="flex items-center gap-1 px-3 py-1.5 bg-[#4ADE80] text-[#111318] text-xs font-medium rounded-[3px]"
                          data-testid="generate-invoice-btn"
                        >
                          <Receipt size={12} /> Generate Invoice
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Executive Summary */}
                {report.executive_summary && (
                  <div className="bg-[#1F222D] border border-[rgba(255,255,255,0.07)] rounded-[3px] p-5">
                    <h4 className="text-white text-sm font-medium mb-2">Executive Summary</h4>
                    <p className="text-[#CBD5E1] text-sm leading-relaxed">{report.executive_summary}</p>
                  </div>
                )}

                {/* Deliverables */}
                {report.deliverables?.length > 0 && (
                  <div className="bg-[#1F222D] border border-[rgba(255,255,255,0.07)] rounded-[3px] overflow-hidden">
                    <div className="flex items-center gap-2 p-4 cursor-pointer hover:bg-[rgba(255,255,255,0.02)]" onClick={() => toggleSection('deliverables')}>
                      {expandedSections.deliverables ? <ChevronDown size={14} className="text-[#8B95A5]" /> : <ChevronRight size={14} className="text-[#8B95A5]" />}
                      <ClipboardCheck size={14} className="text-[#38BDF8]" />
                      <span className="text-white text-sm font-medium">Deliverables ({report.deliverables.length})</span>
                    </div>
                    {expandedSections.deliverables && (
                      <div className="border-t border-[rgba(255,255,255,0.05)]">
                        {report.deliverables.map((d, i) => (
                          <div key={i} className="flex items-center gap-3 px-4 py-3 border-b border-[rgba(255,255,255,0.03)]">
                            {d.status === "completed" ? <CheckCircle2 size={14} className="text-[#4ADE80]" /> :
                             d.status === "partial" ? <AlertTriangle size={14} className="text-[#FBBF24]" /> :
                             <XCircle size={14} className="text-[#F87171]" />}
                            <span className="text-[#CBD5E1] text-sm flex-1">{d.name}</span>
                            <span className="text-xs capitalize" style={{
                              color: d.status === "completed" ? "#4ADE80" : d.status === "partial" ? "#FBBF24" : "#F87171"
                            }}>{d.status}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Gap Analysis vs Proposal */}
                {report.gap_analysis_vs_proposal && (
                  <div className="bg-[#1F222D] border border-[rgba(255,255,255,0.07)] rounded-[3px] p-5">
                    <h4 className="text-white text-sm font-medium mb-3 flex items-center gap-2">
                      <Target size={14} className="text-[#E8A838]" /> Gap Analysis vs Original Proposal
                    </h4>
                    {report.gap_analysis_vs_proposal.scope_coverage && (
                      <p className="text-[#CBD5E1] text-sm mb-2">Scope Coverage: <span className="text-[#38BDF8] font-medium">{report.gap_analysis_vs_proposal.scope_coverage}</span></p>
                    )}
                    {report.gap_analysis_vs_proposal.exclusions?.length > 0 && (
                      <div className="mt-3">
                        <span className="text-[#8B95A5] text-xs uppercase tracking-wider">Exclusions & Justifications</span>
                        {report.gap_analysis_vs_proposal.exclusions.map((ex, i) => (
                          <div key={i} className="mt-2 bg-[#111318] rounded-[3px] p-3">
                            <p className="text-[#F87171] text-sm">{ex.item}</p>
                            <p className="text-[#8B95A5] text-xs mt-1">Justification: {ex.justification}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Remediation Tasks */}
                {report.remediation_tasks?.length > 0 && (
                  <div className="bg-[#1F222D] border border-[rgba(255,255,255,0.07)] rounded-[3px] p-5">
                    <h4 className="text-white text-sm font-medium mb-3">Remediation Tasks</h4>
                    <div className="space-y-2">
                      {report.remediation_tasks.map((t, i) => (
                        <div key={i} className="flex items-start gap-3 bg-[#111318] rounded-[3px] p-3">
                          <span className="text-[#38BDF8] text-xs font-mono mt-0.5">{t.id}</span>
                          <div className="flex-1">
                            <p className="text-[#CBD5E1] text-sm">{t.description}</p>
                            <span className="text-xs capitalize" style={{
                              color: t.priority === "high" ? "#F87171" : t.priority === "medium" ? "#FBBF24" : "#8B95A5"
                            }}>{t.priority} priority</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Approval Section */}
                <div className="bg-[#1F222D] border border-[rgba(255,255,255,0.07)] rounded-[3px] p-5">
                  <h4 className="text-white text-sm font-medium mb-4">Approval Status</h4>
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    {["charstan", "client"].map(role => {
                      const a = approvals[role] || {};
                      return (
                        <div key={role} className="bg-[#111318] rounded-[3px] p-4">
                          <h5 className="text-[#8B95A5] text-xs uppercase tracking-wider mb-2">
                            {role === "charstan" ? t('operations:projectClosure.charstanApproval') : t('operations:projectClosure.clientApproval')}
                          </h5>
                          {a.status === "approve" ? (
                            <div className="flex items-center gap-2 text-[#4ADE80]">
                              <CheckCircle2 size={16} />
                              <span className="text-sm">Approved</span>
                            </div>
                          ) : a.status === "reject" ? (
                            <div className="flex items-center gap-2 text-[#F87171]">
                              <XCircle size={16} />
                              <span className="text-sm">Rejected</span>
                            </div>
                          ) : (
                            <div className="flex items-center gap-2 text-[#FBBF24]">
                              <Clock size={16} />
                              <span className="text-sm">Pending</span>
                            </div>
                          )}
                          {a.comments && <p className="text-[#8B95A5] text-xs mt-2">{a.comments}</p>}
                        </div>
                      );
                    })}
                  </div>

                  {selected.status !== "approved" && (
                    <div className="space-y-3">
                      <textarea
                        value={approvalComment}
                        onChange={e => setApprovalComment(e.target.value)}
                        placeholder={t('operations:projectClosure.addComments')}
                        rows={2}
                        className="w-full px-3 py-2 bg-[#111318] border border-[rgba(255,255,255,0.1)] rounded-[3px] text-white text-sm outline-none resize-none"
                      />
                      <div className="flex gap-3">
                        <button
                          onClick={() => handleApproval("approve", "charstan")}
                          className="flex items-center gap-2 px-4 py-2 bg-[#4ADE80] text-[#111318] text-sm font-medium rounded-[3px]"
                          data-testid="approve-charstan-btn"
                        >
                          <CheckCircle2 size={14} /> Approve as CHARSTAN
                        </button>
                        <button
                          onClick={() => handleApproval("approve", "client")}
                          className="flex items-center gap-2 px-4 py-2 bg-[#38BDF8] text-[#111318] text-sm font-medium rounded-[3px]"
                          data-testid="approve-client-btn"
                        >
                          <CheckCircle2 size={14} /> Approve as Client
                        </button>
                        <button
                          onClick={() => handleApproval("reject", "charstan")}
                          className="flex items-center gap-2 px-4 py-2 bg-[rgba(248,113,113,0.1)] text-[#F87171] text-sm rounded-[3px]"
                        >
                          <XCircle size={14} /> Reject
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="bg-[#1F222D] border border-[rgba(255,255,255,0.07)] rounded-[3px] p-12 text-center">
                <FileCheck className="mx-auto mb-4 text-[#8B95A5]" size={40} />
                <h3 className="text-white text-lg font-['Syne'] font-bold mb-2">Project Closure & Approvals</h3>
                <p className="text-[#8B95A5] text-sm">Select a closure report or generate a new one</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </QMSLayout>
  );
};

export default ProjectClosurePage;
